/**
 * Canonical formatters for market display fields shown in UI surfaces
 * (search modal, market cards, market header, etc.).
 *
 * Status labels are localised via `market.status.*` i18n keys (single source
 * of truth — see `src/i18n/locales/en.json`). The `getStatusConfig` switch
 * inside `market-header.tsx` keeps the same key vocabulary; this helper just
 * pulls the human-readable label without the icon/className baggage.
 */

import type { useTranslations } from 'next-intl';

import { capitalizeLeading } from '@/config/proposal-display';

/** Translator scoped to the `market` namespace via `useTranslations('market')`. */
export type MarketTranslator = ReturnType<typeof useTranslations<'market'>>;

/**
 * Market status values known to have a localised label under `market.status.*`.
 * Keep in sync with `getStatusConfig` in `market-header.tsx` and the keys in
 * `en.json -> market.status`.
 */
const KNOWN_MARKET_STATUSES: ReadonlySet<string> = new Set([
  'created',
  'backing',
  'trading',
  'graduated',
  'resolved',
]);

/**
 * Format a raw market `status` string as a human-readable label.
 *
 * - Known statuses → the localised label from `market.status.<status>`.
 * - Unknown statuses → first-letter-capitalised raw string (graceful fallback,
 *   so a future backend status doesn't render as a dead value).
 *
 * The `tMarket` translator MUST be scoped to the `market` namespace
 * (`useTranslations('market')` or `getTranslations('market')`).
 */
export function formatMarketStatus(status: string, tMarket: MarketTranslator): string {
  if (KNOWN_MARKET_STATUSES.has(status)) {
    return tMarket(`status.${status}` as Parameters<typeof tMarket>[0]);
  }
  return capitalizeLeading(status);
}
